import { usePropertyContext } from "@app/app/hooks";
import { tracker } from "@app/app/services/tracker";
import { ControlledFormField } from "@app/common/components/form/ControlledFormField";
import { Label } from "@app/common/components/form/Label";
import { Button } from "@app/componentLibrary/ui/button";
import { Input } from "@app/componentLibrary/ui/form/Input";
import { LoyaltyContext } from "@app/loyalty/context/LoyaltyContext";
import { tryBookingWhizzLogin } from "@app/loyalty/queries";
import { IBookingWhizzMemberLoginFormFields } from "@app/loyalty/types";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface IBookingWhizzMemberLoginFormProps {}

export const BookingWhizzMemberLoginForm = ({}: IBookingWhizzMemberLoginFormProps) => {
    const { activePropertyId, activeProperty } = usePropertyContext();
    const { setIndex, setAccessToken } = useContext(LoyaltyContext);
    const formMethods = useForm<IBookingWhizzMemberLoginFormFields>({
        defaultValues: {},
        mode: "onChange",
    });

    const [emailValue, setEmailValue] = useState<string>("");
    const [passwordValue, setPasswordValue] = useState<string>("");
    const [isAbleToSubmit, setIsAbleToSubmit] = useState<boolean>(false);
    const { t } = useTranslation(["vendorLoyalty"]);

    useEffect(() => {
        const isEmailValid = emailValue.trim() !== "";
        const isPasswordValid = passwordValue.trim() !== "";

        setIsAbleToSubmit(isEmailValid && isPasswordValid);
    }, [emailValue, passwordValue]);

    const handleSubmit = () => {
        tryBookingWhizzLogin(activePropertyId, emailValue, passwordValue).then((response) => {
            if (!response.success) {
                formMethods.setError("email", {
                    type: "manual",
                    message: response.message || t("login.failureMessage"),
                });
                formMethods.setError("password", {
                    type: "manual",
                    message: response.message || t("login.failureMessage"),
                });

                return;
            }

            // Set access token in context
            setAccessToken(response.accessToken);

            // Clear any previous errors
            formMethods.clearErrors(["email", "password"]);

            tracker.loyaltyRegisteredSuccessfully({ email: emailValue });

            // Set index to profile page
            setIndex("profile");
        });
    };

    return (
        <FormProvider {...formMethods}>
            <div className="text-left mb-3">
                <Label>{t("login.email.label")}</Label>

                <ControlledFormField
                    data-testid="booking-whizz-email-input"
                    rules={{
                        required: t("login.email.errorRequired"),
                        pattern: {
                            value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                            message: t("login.email.errorInvalid"),
                        },
                    }}
                    onChange={(e) => setEmailValue(e.currentTarget.value)}
                    component={Input}
                    autoComplete="email"
                    placeholder={t("login.email.label")}
                    name="email"
                />
            </div>
            <div className="text-left">
                <Label>{t("login.password.label")}</Label>

                <ControlledFormField
                    data-testid="booking-whizz-password-input"
                    rules={{
                        required: t("login.password.errorRequired"),
                    }}
                    onChange={(e) => setPasswordValue(e.currentTarget.value)}
                    component={Input}
                    autoComplete="password"
                    placeholder={t("login.password.label")}
                    name="password"
                    type="password"
                />
            </div>
            <div className="text-left">
                <Button
                    className="bg-transparent active:bg-transparent h-auto text-black mt-2 align p-0 underline text-sm"
                    onClick={() => setIndex("reset_password")}
                >
                    {t("login.forgotPassword")}
                </Button>
            </div>
            <Button
                data-testid="booking-whizz-login-button"
                className="w-full mt-6"
                disabled={!formMethods.formState.isValid || !isAbleToSubmit}
                onClick={handleSubmit}
            >
                {t("login.submit")}
            </Button>
        </FormProvider>
    );
};
